/*
  Thin client for /api/approvals. Every call resolves to { ok, status, data, error, code } and never throws, so
  components can keep their card on screen and show a toast when something fails.
*/
async function request(method, url, body) {
  try {
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body)
    });
    let data = null;
    try {
      data = await response.json();
    } catch (err) {
      data = null;
    }
    return { ok: response.ok, status: response.status, data, error: response.ok ? null : data?.msg || data?.message || `Request failed (${response.status})`, code: data?.code || null };
  } catch (err) {
    return { ok: false, status: 0, data: null, error: err?.message || 'Network error', code: null };
  }
}

export const DOC_TYPE_META = {
  invoice: {
    key: 'invoice',
    label: 'Invoice',
    labelLower: 'invoice',
    plural: 'Invoices',
    apiResource: 'invoices_in',
    detailPath: '/invoices_in',
    pk: 'faktuurinvolgnummer',
    numberField: 'FactuurInNummer',
    tab: 0,
    hasCostType: true,
    hasPurchaseOrders: true
  },
  creditnote: {
    key: 'creditnote',
    label: 'Credit note',
    labelLower: 'credit note',
    plural: 'Credit notes',
    apiResource: 'creditnotes_in',
    detailPath: '/creditnotes_in',
    pk: 'CreditnotaINvolgnummer',
    numberField: 'CreditnotaInNummer',
    tab: 1,
    hasCostType: false,
    hasPurchaseOrders: false
  }
};

export const DOC_TYPES = Object.keys(DOC_TYPE_META);

export const approvalsApi = {
  summary: () => request('GET', '/api/approvals/summary'),
  queue: (docType) => request('GET', `/api/approvals/${docType}/queue`),
  intake: (docType, lane) => request('GET', `/api/approvals/${docType}/intake?lane=${encodeURIComponent(lane)}`),
  card: (docType, id) => request('GET', `/api/approvals/${docType}/${id}`),
  assign: (docType, id, body) => request('POST', `/api/approvals/${docType}/${id}/assign`, body),
  saveAllocations: (docType, id, lines, note) => request('PUT', `/api/approvals/${docType}/${id}/allocations`, { lines, note }),
  approve: (docType, id, body = {}) => request('POST', `/api/approvals/${docType}/${id}/approve`, body),
  reject: (docType, id, reason) => request('POST', `/api/approvals/${docType}/${id}/reject`, { reason }),
  reassign: (docType, id, body) => request('POST', `/api/approvals/${docType}/${id}/reassign`, body),
  reopen: (docType, id, body = {}) => request('POST', `/api/approvals/${docType}/${id}/reopen`, body),
  enrich: (docType, id) => request('POST', `/api/approvals/${docType}/${id}/enrich`),
  linkPurchaseOrder: (id, bestellingId) => request('POST', `/api/approvals/invoice/${id}/purchase-orders`, { bestellingId }),
  unlinkPurchaseOrder: (id, bestellingId) => request('DELETE', `/api/approvals/invoice/${id}/purchase-orders/${bestellingId}`),
  searchPurchaseOrders: (q, supplierId) => request('GET', `/api/approvals/purchase-orders/search?q=${encodeURIComponent(q || '')}${supplierId ? `&supplierId=${supplierId}` : ''}`),
  process: (docType, id, fields) => request('POST', `/api/${DOC_TYPE_META[docType].apiResource}/${id}/process`, fields),
  deleteDocument: (docType, id) => request('DELETE', `/api/${DOC_TYPE_META[docType].apiResource}/${id}`)
};

export function queueLink(docType, id) {
  return `/approvals?tab=${DOC_TYPE_META[docType]?.tab ?? 0}${id ? `&id=${id}` : ''}`;
}
